import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import AiPromptPanel from "./AiPromptPanel";

const AI_DRAWER_WIDTH_DEFAULT = 420;
const AI_DRAWER_WIDTH_MIN = 320;
const AI_DRAWER_WIDTH_MAX_RATIO = 0.9;

export default function GlobalAiAssistantDrawer() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const [open, setOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(AI_DRAWER_WIDTH_DEFAULT);
  const drawerWidthRef = useRef(drawerWidth);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    drawerWidthRef.current = drawerWidth;
  }, [drawerWidth]);

  const handleResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isSmUp) return;
      event.preventDefault();
      resizeRef.current = {
        startX: event.clientX,
        startWidth: drawerWidthRef.current,
      };

      const onMove = (e: PointerEvent) => {
        const state = resizeRef.current;
        if (!state) return;
        const delta = state.startX - e.clientX;
        const maxW = window.innerWidth * AI_DRAWER_WIDTH_MAX_RATIO;
        setDrawerWidth(
          Math.min(maxW, Math.max(AI_DRAWER_WIDTH_MIN, state.startWidth + delta)),
        );
      };

      const onUp = () => {
        resizeRef.current = null;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.removeProperty("cursor");
        document.body.style.removeProperty("user-select");
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [isSmUp],
  );

  useEffect(() => {
    const onToggle = () => setOpen((prev) => !prev);
    const onOpen = () => setOpen(true);
    window.addEventListener("rcm-ai-drawer-toggle", onToggle);
    window.addEventListener("rcm-ai-drawer-open", onOpen);
    return () => {
      window.removeEventListener("rcm-ai-drawer-toggle", onToggle);
      window.removeEventListener("rcm-ai-drawer-open", onOpen);
    };
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      keepMounted
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        "& .MuiDrawer-paper": {
          width: isSmUp ? drawerWidth : "100%",
          maxWidth: "100vw",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isSmUp ? (
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize AI assistant panel"
            onPointerDown={handleResizeStart}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 8,
              cursor: "col-resize",
              zIndex: 2,
              touchAction: "none",
              "&:hover": { bgcolor: "action.hover" },
            }}
          />
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <AiPromptPanel
            sidebarOpen={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
